import { AppEnvironment } from '../../../environments/environment.model';

export interface LandingLogo {
  src: string;
  alt: string;
  link?: string;
}

export interface LandingWorkflowCard {
  id: string;
  title: string;
  description: string;
  variant: 'purple' | 'cyan' | 'blue';
}

export interface LandingComparisonPoint {
  id: string;
  text: string;
  tone: 'good' | 'bad';
}

export interface LandingMetric {
  id: string;
  value: string;
  label: string;
  count?: number | null;
}

export interface LandingUseCaseTeam {
  id: string;
  title: string;
  subtitle: string;
  image: string;
}

export interface LandingPlan {
  id: string;
  name?: string;
  price: string;
  period?: string;
  subline?: string;
  highlight?: 'recommended' | 'save';
  ctaLabel: string;
  bullet?: string;
}

export interface FooterLink {
  id: string;
  label: string;
  href?: string;
  external?: boolean;
  showInAccordion?: boolean;
}

export type FooterContactType = 'email' | 'tel' | 'text';

export interface FooterContactItem {
  id: string;
  label: string;
  type: FooterContactType;
  value?: string;
}

export interface FooterColumn {
  id: string;
  title: string;
  links: FooterLink[];
}

export interface LandingContent {
  marqueeLogos: LandingLogo[];
  contextualSidebarHeadline: {
    leading: string;
    muted: string;
    body: string;
    body2: string;
  };
  workflowHeadline: { leading: string; muted: string; body: string };
  workflowCards: LandingWorkflowCard[];
  comparisonHeadline: { leading: string; muted: string };
  comparisonGeneric: LandingComparisonPoint[];
  comparisonPcChat: LandingComparisonPoint[];
  metrics: LandingMetric[];
  useCases: { headingLeading: string; headingMuted: string; body: string };
  useCaseTeams: LandingUseCaseTeam[];
  plans: {
    heading: string;
    body: string;
    cards: LandingPlan[];
    includes: string[];
  };
  finalCta: { heading: string; body: string };
  footerLeft: {
    addressLines: string[];
    contactLines: FooterContactItem[];
  };
  footerRightColumns: FooterColumn[];
  footerSocial: { linkedin: string; x: string; instagram: string };
}

export function createMarqueeLogos(count = 27): LandingLogo[] {
  return Array.from({ length: count }, (_, idx) => {
    const n = String(idx + 1).padStart(2, '0');
    return { src: `/landing/marquee/logo-${n}.png`, alt: 'Customer logo' };
  });
}

export const CONTEXTUAL_SIDEBAR_HEADLINE: LandingContent['contextualSidebarHeadline'] = {
  leading: 'PrivateCircle Intelligence ',
  muted: 'Everywhere',
  body: 'PC AI Chat brings together verified data context to every chat,provides grounded reasoning and generates structured outputsfor your workflows.',
  body2:
    'The <strong>"Contextual Sidebar"</strong> extension on your Chrome browser allows you to trigger chats directly with a right click.',
};

export const WORKFLOW_HEADLINE: LandingContent['workflowHeadline'] = {
  leading: 'The Full Spectrum of Private Market Data',
  muted: '',
  body: '',
};

export const WORKFLOW_CARDS: LandingWorkflowCard[] = [
  {
    id: 'company-research',
    title: 'Company Research',
    description:
      'Access detailed financials (Revenue, EBITDA, PAT), multi-year historical trends, shareholding patterns, and board of directors for over 2 million Indian entities.',
    variant: 'purple',
  },
  {
    id: 'deals-valuations',
    title: 'Deals & Valuations',
    description:
      'Search for funding rounds (Seed to Late Stage), M&A, and IPOs. Analyze valuation multiples such as EV/Revenue, EV/EBITDA, and post-money valuations.',
    variant: 'blue',
  },
  {
    id: 'investor-intel',
    title: 'Investor Intelligence',
    description:
      'Research VC/PE funds, Family Offices, and HNIs. Filter by AUM, sector preferences, investment stages, and active portfolio company counts.',
    variant: 'cyan',
  },
  {
    id: 'mca-compliance',
    title: 'MCA & Compliance',
    description:
      'Retrieve official MCA Master Data, monitor open charges/loans, check insolvency status, and identify defaulters.',
    variant: 'blue',
  },
  {
    id: 'market-peer',
    title: 'Market & Peer Analysis',
    description:
      'Perform competitive benchmarking, identify industry peers, and analyze market trends/sizing using a blend of PrivateCircle data and web research.',
    variant: 'cyan',
  },
  {
    id: 'governance-team',
    title: 'Governance Team',
    description:
      'Fetch management team details, executive remuneration, employee headcount, and cross-directorships.',
    variant: 'purple',
  },
];

export const COMPARISON_HEADLINE: LandingContent['comparisonHeadline'] = {
  leading: "Don't risk your deal on a",
  muted: 'Hallucination.',
};

export const COMPARISON_GENERIC: LandingComparisonPoint[] = [
  {
    id: 'generic-1',
    tone: 'bad',
    text: 'Cites secondary sources like blogs, news, and outdated press releases, based on model cut off dates.',
  },
  {
    id: 'generic-2',
    tone: 'bad',
    text: 'Blind to data behind government paywalls; lacks visibility into India private companies beyond what is already in public domain.',
  },
  {
    id: 'generic-3',
    tone: 'bad',
    text: 'Probabilistic and may fail at complex financial ratio calculations.',
  },
  {
    id: 'generic-4',
    tone: 'bad',
    text: 'Broad generalist; misses niche Indian corporate nuances.',
  },
];

export const COMPARISON_PC_CHAT: LandingComparisonPoint[] = [
  {
    id: 'pc-1',
    tone: 'good',
    text: 'Built on Primary Sources (MCA, NCLT, EPFO, and Stock Exchanges). Auditable sources extracted directly from official filings.',
  },
  {
    id: 'pc-2',
    tone: 'good',
    text: 'PrivateCircle Research data is trusted by top professionals since 2014 for any unlisted companies/deals data.',
  },
  {
    id: 'pc-3',
    tone: 'good',
    text: 'Deterministic Logic using structured financial workflows to compute ratios and other metrics.',
  },
  {
    id: 'pc-4',
    tone: 'good',
    text: 'Domain Specialist that deeply understands Indian private market data nuances.',
  },
];

export const METRICS: LandingMetric[] = [
  { id: 'm1', value: '', count: null, label: 'COMPANIES TRACKED' },
  { id: 'm2', value: '', count: null, label: 'DEALS COVERED' },
  { id: 'm3', value: '', count: null, label: 'VC/PE FUNDS PROFILED' },
  { id: 'm4', value: '', count: null, label: 'DEBT CHARGES' },
];

export const USE_CASES: LandingContent['useCases'] = {
  headingLeading: 'Tailored Intelligence',
  headingMuted: 'Every Team',
  body: 'AskPC provides expert-level insights tailored for your core investment and strategy teams.',
};

export const USE_CASE_TEAMS: LandingUseCaseTeam[] = [
  {
    id: 'investment',
    title: 'Investment Teams',
    subtitle: 'VC / PE / Family Offices',
    image: '/landing/use-cases/investment_teams.svg',
  },
  {
    id: 'strategy',
    title: 'Strategy & Corp Dev',
    subtitle: 'Corporate Development Teams',
    image: '/landing/use-cases/strategy_corp_dev.svg',
  },
  {
    id: 'sales',
    title: 'Sales & Business Dev',
    subtitle: 'Business Development Teams',
    image: '/landing/use-cases/sales_business_dev.svg',
  },
  {
    id: 'lending',
    title: 'Lending & Credit Teams',
    subtitle: 'Banks / NBFCs',
    image: '/landing/use-cases/lending_credit_teams.svg',
  },
];

export const PLANS: LandingContent['plans'] = {
  heading: 'Choose Your Plan',
  body: 'Start with 5 million free tokens. Scale as you grow with flexible pricing that fits your needs.',
  cards: [
    {
      id: 'plan-one',
      price: '₹0',
      subline: '5 million tokens',
      bullet: 'Free Trial',
      ctaLabel: 'Get Started',
    },
    {
      id: 'plan-two',
      price: '₹6,999',
      subline: '30 million tokens',
      bullet: 'For occasional usage',
      ctaLabel: 'Get Started',
    },
    {
      id: 'plan-three',
      price: '₹20,000',
      subline: '120 million tokens',
      bullet: 'Best for regular usage',
      ctaLabel: 'Get Started',
      highlight: 'recommended',
    },
    {
      id: 'plan-four',
      price: '₹60,000',
      subline: '400 million tokens',
      bullet: 'Best value - Save 25%',
      ctaLabel: 'Get Started',
      highlight: 'save',
    },
  ],
  includes: [
    '1.7M+ company database',
    'Verified private market data',
    'Chrome Extension',
    '100% cited sources',
  ],
};

export const FINAL_CTA: LandingContent['finalCta'] = {
  heading: 'Your first 5 million tokens are on us. Start asking',
  body: 'See the difference real-time, verified data makes in your research workflow today.',
};

export const FOOTER_LEFT: LandingContent['footerLeft'] = {
  addressLines: [
    'PrivateCircle, MKB Plaza - 4th Floor,',
    '7th Main, Motappapalya, Indiranagar, Bengaluru - 560008.',
  ],
  contactLines: [
    {
      id: 'email',
      label: 'support@privatecircle.co',
      type: 'email',
      value: 'support@privatecircle.co',
    },
    { id: 'p1', label: '+91 6364900795', type: 'tel', value: '+916364900795' },
    { id: 'p2', label: '+91 6364900796', type: 'tel', value: '+916364900796' },
    { id: 'p3', label: '+91 6364942319', type: 'tel', value: '+916364942319' },
  ],
};

export const FOOTER_SOCIAL: LandingContent['footerSocial'] = {
  linkedin: 'https://www.linkedin.com/company/privatecircle',
  x: 'https://twitter.com/_privatecircle',
  instagram: 'https://www.instagram.com/privatecircle_research',
};

export function createFooterRightColumns(
  env: Pick<
    AppEnvironment,
    'privateCircleBaseUrl' | 'networksBaseUrl' | 'marketsBaseUrl' | 'blogBaseUrl'
  >,
): FooterColumn[] {
  return [
    {
      id: 'products',
      title: 'PC Products',
      links: [
        {
          id: 'research',
          label: 'Research',
          href: `${env.privateCircleBaseUrl}/research`,
          external: true,
        },
        {
          id: 'connect',
          label: 'Connect',
          href: `${env.privateCircleBaseUrl}/connect`,
          external: true,
        },
        { id: 'markets', label: 'Markets', href: `${env.marketsBaseUrl}/homepage`, external: true },
        {
          id: 'networks',
          label: 'Networks',
          href: `${env.networksBaseUrl}/homepage`,
          external: true,
        },
        { id: 'api', label: 'API', href: `${env.privateCircleBaseUrl}/pc-api`, external: true },
      ],
    },
    {
      id: 'platform',
      title: 'Platform',
      links: [
        { id: 'blogs', label: 'Blogs / Reports', href: env.blogBaseUrl, external: true },
        {
          id: 'people',
          label: 'Our People',
          href: `${env.privateCircleBaseUrl}/our-people`,
          external: true,
        },
        { id: 'press', label: 'Press', href: `${env.privateCircleBaseUrl}/press`, external: true },
        {
          id: 'careers',
          label: 'Careers',
          href: `${env.privateCircleBaseUrl}/our-people`,
          external: true,
        },
      ],
    },
    {
      id: 'legal',
      title: 'Legal',
      links: [
        {
          id: 'privacy',
          label: 'Privacy Policy',
          href: `${env.privateCircleBaseUrl}/privacy-policy`,
          external: true,
        },
        {
          id: 'terms',
          label: 'Terms & conditions',
          href: `${env.privateCircleBaseUrl}/terms-conditions`,
          external: true,
        },
        { id: 'disclaimer', label: 'Disclaimer', href: '/disclaimer', external: false },
      ],
    },
  ];
}

export function createLandingContent(
  env: Pick<
    AppEnvironment,
    'privateCircleBaseUrl' | 'networksBaseUrl' | 'marketsBaseUrl' | 'blogBaseUrl'
  >,
): LandingContent {
  return {
    marqueeLogos: createMarqueeLogos(27),
    contextualSidebarHeadline: CONTEXTUAL_SIDEBAR_HEADLINE,
    workflowHeadline: WORKFLOW_HEADLINE,
    workflowCards: WORKFLOW_CARDS,
    comparisonHeadline: COMPARISON_HEADLINE,
    comparisonGeneric: COMPARISON_GENERIC,
    comparisonPcChat: COMPARISON_PC_CHAT,
    metrics: METRICS,
    useCases: USE_CASES,
    useCaseTeams: USE_CASE_TEAMS,
    plans: PLANS,
    finalCta: FINAL_CTA,
    footerLeft: FOOTER_LEFT,
    footerRightColumns: createFooterRightColumns(env),
    footerSocial: FOOTER_SOCIAL,
  };
}
